import React from "react";
import { Button, Chip } from "@heroui/react";
import {
  MapPinIcon,
  VideoCameraIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  SparklesIcon,
  FaceSmileIcon,
  PlayIcon
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import CenteredHeroNav from "../components/CenteredHeroNav";
import { useAuth } from "../contexts/AuthContext";

const GetStartedButton = ({ onTriggerLogin }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const handleClick = () => {
    if (currentUser) {
      navigate('/locations');
    } else {
      onTriggerLogin();
    }
  };

  return (
    <Button
      color="primary"
      size="lg"
      onPress={handleClick}
      className="text-lg px-10 mx-auto lg:mx-0"
      endContent={<ArrowRightIcon className="w-5 h-5" />}
    >
      {currentUser ? 'Watch Videos' : 'Get Started'}
    </Button>
  );
};

const HomeHeroUI = () => {
  const [triggerLogin, setTriggerLogin] = React.useState(false);

  return (
    <div className="min-h-screen flex flex-col theme-gradient-surface">
      <CenteredHeroNav triggerLogin={triggerLogin} />

      {/* Accent Banner */}
      <div className="w-full bg-gradient-to-r from-primary-500 to-secondary-500 py-3 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-white">
          <SparklesIcon className="w-5 h-5" />
          <span className="font-semibold">Share moments from everywhere—road, sky, sea, and beyond</span>
        </div>
      </div>

      <div className="flex-1 px-6 sm:px-8 lg:px-12 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white dark:bg-slate-800 shadow-md mb-8 dark:border dark:border-slate-700/50">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-sm font-semibold text-gray-700 dark:text-slate-200">Video Sharing Platform</span>
              </div>

              <h1 className="text-6xl font-black mb-6 text-gray-900 dark:text-slate-50 leading-tight">
                Share your moments. From road to sky to sea.
              </h1>

              <p className="text-xl text-gray-600 dark:text-slate-300 mb-10 leading-relaxed">
                ClipKite lets you share video moments from anywhere—dashcams, boats, planes, scenic drives, or everyday adventures. Upload with location tags to promote safety, share memorable experiences, or capture life's unexpected moments!
              </p>

              <div className="grid grid-cols-3 gap-6 mb-10">
                <div className="text-center">
                  <MapPinIcon className="w-8 h-8 mx-auto mb-2 text-primary-600 dark:text-indigo-400" />
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-300">Location Tagging</p>
                </div>
                <div className="text-center">
                  <VideoCameraIcon className="w-8 h-8 mx-auto mb-2 text-primary-600 dark:text-indigo-400" />
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-300">Video Upload</p>
                </div>
                <div className="text-center">
                  <ShieldCheckIcon className="w-8 h-8 mx-auto mb-2 text-primary-600 dark:text-indigo-400" />
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-300">Safety First</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 items-center mb-8">
                <Chip size="md" variant="flat" color="default" startContent={<VideoCameraIcon className="w-4 h-4" />}>
                  Scenery & Travel
                </Chip>
                <Chip size="md" variant="flat" color="default" startContent={<FaceSmileIcon className="w-4 h-4" />}>
                  Funny Moments
                </Chip>
                <Chip size="md" variant="flat" color="default" startContent={<ShieldCheckIcon className="w-4 h-4" />}>
                  Safety Incidents
                </Chip>
              </div>

              <GetStartedButton onTriggerLogin={() => setTriggerLogin(true)} />
            </div>

            {/* Right Visual */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white dark:bg-slate-800 rounded-3xl p-12 shadow-2xl dark:border dark:border-slate-700/50">
                  <div className="space-y-6">
                    <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-5/6"></div>
                    <div className="h-48 bg-gradient-to-br from-primary-100 to-secondary-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center">
                      <PlayIcon className="w-20 h-20 text-primary-600 dark:text-indigo-400" />
                    </div>
                    <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-2/3"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeHeroUI;
